import React, { useContext, useEffect, useMemo, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { toast } from "../../utils/toast";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/modals/Modal";
import AppContext from "../../context/AppContext";
import {
  fetchData,
  deleteWorkflow,
  fetchWorkflowData,
} from "../../service/workflow";
import DeletePermission from "../../components/suspence/DeletePermission";
import {
  ArrowUpDown,
  Check,
  ClipboardList,
  CopyPlus,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Trash2,
} from "lucide-react";
import { Spinner } from "../../Spinner";
import translations from "../../components/common/Translations";
import { getPermissionByScreenCode } from "../../utils/permissions";
import useDirection from "../../hooks/useDirection";
import ActionIconButton from "../../components/common/ActionIconButton";
import CommonButton from "../../components/common/CommonButton";

const SORT_OPTIONS = [
  { key: "nameAsc", labelKey: "sortNameAsc", fallback: "Name (A-Z)" },
  { key: "nameDesc", labelKey: "sortNameDesc", fallback: "Name (Z-A)" },
  { key: "updatedNewest", labelKey: "sortUpdatedNewest", fallback: "Last updated (newest)" },
  { key: "updatedOldest", labelKey: "sortUpdatedOldest", fallback: "Last updated (oldest)" },
];

const getWorkflowTimestamp = (item) => {
  const raw = item?.updatedAt || item?.createdAt;
  const time = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
};

const WorkflowMain = () => {
  const [dynamicWorkflow, setdynamicWorkflow] = useState([]);
  const [getFormData, setgetFormData] = useState([]);
  const [open, setOpen] = useState(false);
  const [clone, setClone] = useState(false);
  const [refreshPage] = useState(false); // New state variable
  const navigate = useNavigate();
  const { theme, selectedOrgId, langMode, userType, projectId } =
    useContext(AppContext);
  const { isRTL: isRtl, dir } = useDirection();
  const themeClass = theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "";
  const workflowLabel = translations[langMode].workflowe;
  const cloneWorkflowTooltip = `${translations[langMode].Clone} ${workflowLabel}`;
  const editWorkflowTooltip = `${translations[langMode].edit} ${workflowLabel}`;
  const deleteWorkflowTooltip = `${translations[langMode].delete} ${workflowLabel}`;
  const workflowPerm = getPermissionByScreenCode("WORKFLOW");
  const canCreateWorkflow = userType === "super_admin" || Boolean(workflowPerm?.canCreate);
  const canUpdateWorkflow = userType === "super_admin" || Boolean(workflowPerm?.canUpdate);
  const canDeleteWorkflow = userType === "super_admin" || Boolean(workflowPerm?.canDelete);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [sortOption, setSortOption] = useState(null);

  const isDefaultView = !searchText.trim() && !sortOption;

  const handleClearSearchAndSort = () => {
    setSearchText("");
    setSortOption(null);
  };

  const handleWorkflowscreen = () => {
    if (!selectedOrgId && userType === "super_admin") {
      toast.warn("select orgnization first");
      return;
    }
    navigate("/workflow-form");
  };

  const displayedWorkflows = useMemo(() => {
    const source = Array.isArray(dynamicWorkflow) ? dynamicWorkflow : [];
    const query = searchText.trim().toLowerCase();
    const filtered = query
      ? source.filter((item) => (item.workflowName || "").toLowerCase().includes(query))
      : [...source];

    if (!sortOption) return filtered;

    return filtered.sort((a, b) => {
      switch (sortOption) {
        case "nameAsc":
          return (a.workflowName || "").localeCompare(b.workflowName || "");
        case "nameDesc":
          return (b.workflowName || "").localeCompare(a.workflowName || "");
        case "updatedOldest":
          return getWorkflowTimestamp(a) - getWorkflowTimestamp(b);
        case "updatedNewest":
        default:
          return getWorkflowTimestamp(b) - getWorkflowTimestamp(a);
      }
    });
  }, [dynamicWorkflow, searchText, sortOption]);

  const activeSortOption = SORT_OPTIONS.find((option) => option.key === sortOption);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchData(projectId, userType);
      setdynamicWorkflow(data);
      setLoading(false);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [refreshPage, projectId]);

  const handleDeleteClick = (workflowId) => {
    setWorkflowToDelete(workflowId);
    setShowDeleteModal(true);
  };

  const handleDeleteWorkflow = async (workflowId) => {
    try {
      const successMessage = await deleteWorkflow(workflowId);
      toast.success(successMessage);
      const updatedDynamicWorkflow = dynamicWorkflow.filter(
        (item) => item.workflowId !== workflowId
      );
      setdynamicWorkflow(updatedDynamicWorkflow);
      setShowDeleteModal(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEditClick = async (workflowId) => {
    try {
      const {
        workflowFields,
        existWorkflowName,
        existPdfTitleName,
        workflowTimer,
        existDragItems,
        existRadioButtons,
        accountProtectionCheck,
        clockin,
        clockout,
        isPageWise,
        // ------- sakshi -------- start
        isReviewEnabled,
        // ------- sakshi -------- end
        isShowDistance,
        isSafetyComplianceRequired,
        safetyWorkflowId,
      } = await fetchWorkflowData(workflowId);
      // companyStamp URL comes from the get-workflow-list response.
      const existCompanyStamp =
        dynamicWorkflow.find((w) => w.workflowId === workflowId)?.companyStamp ||
        "";
      navigate("/workflow-form", {
        state: {
          workflowId,
          workflowFields,
          existWorkflowName,
          existPdfTitleName,
          workflowTimer,
          existDragItems,
          existRadioButtons,
          accountProtectionCheck,
          clockin,
          clockout,
          isPageWise,
          // ------- sakshi -------- start
          isReviewEnabled,
          // ------- sakshi -------- end
          isShowDistance,
          isSafetyComplianceRequired,
          safetyWorkflowId,
          existCompanyStamp,
          isEdit: true,
        },
      });
    } catch (error) {
      console.error(error.message);
    }
  };

  const handleCloneClick = async (workflowId) => {
    try {
      const {
        workflowFields,
        existPdfTitleName,
        existWorkflowName,
        existDragItems,
        existRadioButtons,
        isPageWise,
        // ------- sakshi -------- start
        isReviewEnabled,
        // ------- sakshi -------- end
        isShowDistance,
        isSafetyComplianceRequired,
      } = await fetchWorkflowData(workflowId);
      navigate("/workflow-form", {
        state: {
          workflowFields,
          existWorkflowName,
          existPdfTitleName,
          existDragItems,
          existRadioButtons,
          isPageWise,
          // ------- sakshi -------- start
          isReviewEnabled,
          // ------- sakshi -------- end
          isShowDistance,
          isSafetyComplianceRequired,
          // The clone target is a brand-new workflow, but we still need the source
          // id to read its safety form and copy it across.
          safetySourceWorkflowId: workflowId,
          isClone: true,
        },
      });
    } catch (error) {
      console.error(error.message);
    }
  };

  return (
    <>
      <div className={`flex min-h-full sm:h-full sm:min-h-0 w-full flex-col overflow-y-auto sm:overflow-hidden ${themeClass}`} dir={dir}>
        {/* Header card: heading + subheading + primary actions */}
        <div
          className={`mx-2 mt-2 shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm flex flex-col gap-3 sm:mx-4 sm:mt-3 sm:px-5 sm:py-4 sm:gap-4 ${themeClass}`}
        >
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="min-w-0 flex-1 basis-52">
              <h1 className="text-base font-bold text-slate-800 leading-tight sm:text-lg">
                {translations[langMode].workflowe}
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {translations[langMode]?.workflowManagementSubtitle ??
                  "Create and manage the workflow templates used to configure task forms."}
              </p>
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              {canCreateWorkflow && (
                <CommonButton colorIntent="primary"
                  onClick={handleWorkflowscreen}
                  variant="primary"
                  tooltipText={
                    selectedOrgId
                      ? translations[langMode].createnewworkflows
                      : translations[langMode].selectorgtocreateaworkflow
                  }
                  className="!h-10 !min-h-[40px] flex-1 sm:flex-initial justify-center !rounded-[10px] !px-3 sm:!px-4 !py-2 text-xs sm:text-sm"
                >
                  <Plus size={18} />{" "}
                  <span className="whitespace-nowrap">{translations[langMode].createWorkflow}</span>
                </CommonButton>
              )}
            </div>
          </div>
        </div>

        {/* Body card: scrollable workflow list */}
        <div
          className={`mx-2 mb-2 mt-2 flex min-h-[22rem] sm:min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-4 sm:mb-3 sm:mt-3 ${themeClass}`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2.5 px-3 py-2.5 border-b border-slate-100 bg-white shrink-0 sm:gap-3 sm:px-5">
            <p className="text-xs font-medium text-slate-500">
              {loading ? "—" : displayedWorkflows.length} {translations[langMode]?.workflows ?? "Workflows"}
            </p>
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <div className="relative flex-1 min-w-0 sm:w-[220px] sm:flex-initial">
                <Search
                  size={14}
                  className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-400 ${isRtl ? "right-3" : "left-3"}`}
                />
                <input
                  type="text"
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder={translations[langMode]?.searchWorkflow ?? "Search workflow name..."}
                  className={`h-9 w-full rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] ${isRtl ? "pr-9 pl-3 text-right" : "pl-9 pr-3"}`}
                />
              </div>

              <Menu as="div" className="relative shrink-0">
                <Menu.Button
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium sm:px-3 sm:text-sm"
                  style={{
                    borderColor: sortOption ? "#93C5FD" : "#E2E8F0",
                    background: sortOption ? "#EFF6FF" : "white",
                    color: sortOption ? "#1D4ED8" : "#475569",
                  }}
                >
                  <ArrowUpDown size={14} />
                  <span className="hidden sm:inline whitespace-nowrap">
                    {activeSortOption
                      ? translations[langMode]?.[activeSortOption.labelKey] ?? activeSortOption.fallback
                      : translations[langMode]?.sortBy ?? "Sort"}
                  </span>
                </Menu.Button>
                <Transition
                  as={React.Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items
                    className={`absolute z-[60] mt-1.5 w-56 rounded-xl border border-slate-100 bg-white py-1.5 shadow-xl focus:outline-none ${
                      isRtl ? "left-0 origin-top-left" : "right-0 origin-top-right"
                    }`}
                  >
                    {SORT_OPTIONS.map((option) => (
                      <Menu.Item key={option.key}>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => setSortOption(option.key)}
                            className={`flex w-full items-center justify-between gap-2.5 px-3.5 py-2 mx-1 rounded-lg text-sm font-medium text-start ${
                              active ? "bg-slate-50" : ""
                            } ${sortOption === option.key ? "text-[#236DB4]" : "text-slate-700"}`}
                            style={{ width: "calc(100% - 0.5rem)" }}
                          >
                            {translations[langMode]?.[option.labelKey] ?? option.fallback}
                            {sortOption === option.key && <Check size={15} />}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>

              <button
                type="button"
                onClick={handleClearSearchAndSort}
                disabled={isDefaultView}
                className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-2.5 text-xs font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50 sm:px-3"
              >
                <RotateCcw size={13} />
                <span className="hidden sm:inline">{translations[langMode]?.clear ?? "Clear"}</span>
              </button>
            </div>
          </div>

          <div className="scrollbar-container task-list-scrollbar relative min-h-0 w-full flex-1 overflow-y-auto overflow-x-hidden px-3 py-3 sm:px-5 sm:py-4">
            {loading && <Spinner />}
            {displayedWorkflows.length > 0 ? (
              <ul className="space-y-2.5 sm:space-y-3 list-none m-0 p-0">
                {displayedWorkflows.map((item, index) => (
                  <li
                    key={item.workflowId ?? index}
                    className={`
            flex flex-row items-center justify-between gap-3 min-w-0
            p-3 sm:p-4 rounded-xl border border-slate-200/90 bg-white shadow-xs
            hover:border-[#236DB4]/50 hover:shadow-md transition-all duration-200
            ${themeClass}
          `}
                  >
                    <div className="flex items-center gap-2.5 sm:gap-3 flex-1 min-w-0">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[#236DB4] text-xs font-bold border border-blue-100 dark:bg-gray-700 dark:border-gray-600 dark:text-blue-400">
                        {index + 1}
                      </span>
                      <div className="flex flex-col text-start min-w-0 flex-1" dir="auto">
                        <p className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-gray-100 truncate" dir="auto" title={item.workflowName}>
                          {item.workflowName}
                        </p>
                        <p className="text-[11px] sm:text-xs text-slate-400 dark:text-gray-400 mt-0.5 truncate">
                          {translations[langMode].updatedon}{" "}
                          {item.updatedAt
                            ? new Date(item.updatedAt).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                            : new Date(item.createdAt).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                        </p>
                      </div>
                    </div>
                    {(canCreateWorkflow || canUpdateWorkflow || canDeleteWorkflow) && (
                      <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
                        {canCreateWorkflow && (
                          <ActionIconButton
                            onClick={() => handleCloneClick(item.workflowId)}
                            tone="gray"
                            ariaLabel={cloneWorkflowTooltip}
                            tooltipText={cloneWorkflowTooltip}
                            className="!h-8 !w-8 sm:!h-9 sm:!w-9"
                          >
                            <CopyPlus size={16} />
                          </ActionIconButton>
                        )}
                        {canUpdateWorkflow && (
                          <ActionIconButton
                            onClick={() =>
                              handleEditClick(item.workflowId, item.workflowName, item.timer)
                            }
                            tone="blue"
                            ariaLabel={editWorkflowTooltip}
                            tooltipText={editWorkflowTooltip}
                            className="!h-8 !w-8 sm:!h-9 sm:!w-9"
                          >
                            <Pencil size={16} />
                          </ActionIconButton>
                        )}
                        {canDeleteWorkflow && (
                          <ActionIconButton
                            onClick={() => handleDeleteClick(item.workflowId)}
                            tone="red"
                            ariaLabel={deleteWorkflowTooltip}
                            tooltipText={deleteWorkflowTooltip}
                            className="!h-8 !w-8 sm:!h-9 sm:!w-9"
                          >
                            <Trash2 size={16} />
                          </ActionIconButton>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mx-auto mt-3 w-full max-w-2xl rounded-2xl border border-dashed border-[#BEDAF0] bg-gradient-to-b from-[#F8FBFF] to-white px-6 py-10 text-center">
                <span className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#236DB4] shadow-sm ring-1 ring-[#D9EAF9]">
                  <ClipboardList size={22} />
                </span>
                <h3 className="text-[15px] font-semibold tracking-[0.005em] text-slate-800 sm:text-[16px]">
                  {!projectId
                    ? (translations[langMode]?.workflowEmptyNoProjectTitle ?? "Select a project to get started")
                    : searchText.trim()
                      ? (translations[langMode]?.workflowEmptySearchTitle ?? "No workflows found")
                      : (translations[langMode]?.workflowEmptyTitle ?? "Create a workflow to see them here")}
                </h3>
                <p className="mx-auto mt-2 max-w-xl text-[13px] leading-6 text-slate-500">
                  {!projectId
                    ? (translations[langMode]?.workflowEmptyNoProjectDesc ?? "Please select a project to view and manage workflows.")
                    : searchText.trim()
                      ? (translations[langMode]?.noWorkflowsMatchSearch ?? "No workflows match your search")
                      : (translations[langMode]?.workflowEmptyDesc ?? "Build your first workflow template to configure task forms faster and consistently.")}
                </p>
                {!searchText.trim() && projectId && canCreateWorkflow && (
                  <div className="mt-5">
                    <CommonButton colorIntent="primary"
                      onClick={handleWorkflowscreen}
                      variant="primary"
                      tooltipText={
                        selectedOrgId
                          ? translations[langMode].createnewworkflows
                          : translations[langMode].selectorgtocreateaworkflow
                      }
                      className="!h-10 !rounded-[10px] !px-4 text-sm"
                    >
                      <Plus size={18} />
                      {translations[langMode].createWorkflow}
                    </CommonButton>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <Modal
          open={open}
          setOpen={setOpen}
          clone={clone}
          setClone={setClone}
          getFormData={getFormData}
          loadData={loadData}
        />
        {showDeleteModal && (
          <DeletePermission
            onClose={() => setShowDeleteModal(false)}
            deleteFunction={() => handleDeleteWorkflow(workflowToDelete)}
            onDeleteText={translations[langMode].rusurewanttodeleteworkflow}
          />
        )}
      </div>
    </>
  );
};

export default WorkflowMain;
